import type { ClientOperatingContextSummary } from "@solomon-apps/shared";
import { useOrgAuthStore } from "@solomon-apps/shared";
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";

import { activeKeyCount } from "@/pages/ApiKeys/apiKeyPageModel";
import type { ClientApiKeyMetadata, CreateClientApiKeyInput, RotateClientApiKeyInput } from "@/services/clientApiKeys";
import { OrgApiError } from "@/services/orgApiFetch";
import { isAmbiguousIssuanceError, isCurrentClientApiContext } from "@/utils/clientApiKeyWorkflow";

import { useClientApiKeys } from "./useClientApiKeys";

function errorMessage(error: unknown): string {
    return error instanceof Error ? error.message : "The request failed";
}
interface UseApiKeyPageWorkflowResult {
    issuerClientUuid: string | null;
    query: UseQueryResult<ClientApiKeyMetadata[], Error>;
    queryErrorMessage: string;
    keys: ClientApiKeyMetadata[];
    selectedContext: ClientOperatingContextSummary | null;
    activeCount: number;
    issuanceBlocked: boolean;
    rotationBlocked: boolean;
    issuedSecret: { contextUuid: string; value: string; rotation: { label: string; endsAt: string } | null } | null;
    secret: string | null;
    issuanceWarning: string | null;
    createError: string | null;
    revokeError: string | null;
    rotateError: string | null;
    scopedRevokeTarget: ClientApiKeyMetadata | null;
    scopedRotateTarget: ClientApiKeyMetadata | null;
    isCreating: boolean;
    rotatingKeyIds: Set<string>;
    revokeMutation: UseMutationResult<void, Error, { contextUuid: string; id: string }, unknown>;
    createKey: (input: CreateClientApiKeyInput) => Promise<boolean>;
    rotateKey: (id: string, input: RotateClientApiKeyInput) => Promise<boolean>;
    revokeKey: (id: string) => Promise<void>;
    discardSecret: () => void;
    openRevoke: (key: ClientApiKeyMetadata) => void;
    closeRevoke: () => void;
    openRotate: (key: ClientApiKeyMetadata) => void;
    closeRotate: () => void;
}

export function useApiKeyPageWorkflow(enabled: boolean): UseApiKeyPageWorkflowResult {
    const contexts = useOrgAuthStore((state) => state.availableClientContexts);
    const {
        issuerClientUuid,
        query,
        createKey: issueKey,
        isCreating,
        rotateKey: issueReplacement,
        rotatingKeyIds,
        revokeMutation
    } = useClientApiKeys(enabled);
    const [issuedSecret, setIssuedSecret] = useState<{
        contextUuid: string;
        value: string;
        rotation: { label: string; endsAt: string } | null;
    } | null>(null);
    const [createError, setCreateError] = useState<string | null>(null);
    const [issuanceWarning, setIssuanceWarning] = useState<string | null>(null);
    const [revokeTarget, setRevokeTarget] = useState<ClientApiKeyMetadata | null>(null);
    const [revokeError, setRevokeError] = useState<string | null>(null);
    const [rotateTarget, setRotateTarget] = useState<ClientApiKeyMetadata | null>(null);
    const [rotateError, setRotateError] = useState<string | null>(null);

    const selectedContext = useMemo(
        () => contexts.find((context) => context.issuerClientUuid === issuerClientUuid) ?? null,
        [contexts, issuerClientUuid]
    );
    const keys = query.data ?? [];
    const secret = issuedSecret?.contextUuid === issuerClientUuid ? issuedSecret.value : null;
    const scopedRevokeTarget = revokeTarget?.issuerClientUuid === issuerClientUuid ? revokeTarget : null;
    const scopedRotateTarget = rotateTarget?.issuerClientUuid === issuerClientUuid ? rotateTarget : null;
    const activeCount = activeKeyCount(keys);
    const contextBlocksIssuance =
        selectedContext?.status !== "Active" ||
        selectedContext.issuerCapabilityStatus !== "Active" ||
        selectedContext.readOnly ||
        query.isLoading ||
        query.isFetching;
    // `reset` is bound once per MutationObserver, so it is stable across renders.
    const { reset: resetRevokeMutation } = revokeMutation;

    useEffect(() => {
        setIssuedSecret(null);
        setCreateError(null);
        setIssuanceWarning(null);
        setRevokeTarget(null);
        setRevokeError(null);
        setRotateTarget(null);
        setRotateError(null);
        resetRevokeMutation();
    }, [issuerClientUuid, resetRevokeMutation]);

    const createKey = async (input: CreateClientApiKeyInput): Promise<boolean> => {
        const operationContextUuid = issuerClientUuid;
        if (!operationContextUuid) return false;
        setCreateError(null);
        setIssuanceWarning(null);
        try {
            const result = await issueKey(operationContextUuid, input);
            if (
                !isCurrentClientApiContext(operationContextUuid, useOrgAuthStore.getState().selectedClientContextUuid)
            ) {
                return false;
            }
            setIssuedSecret({
                contextUuid: operationContextUuid,
                value: result.secret,
                rotation: null
            });
            return true;
        } catch (error) {
            if (
                !isCurrentClientApiContext(operationContextUuid, useOrgAuthStore.getState().selectedClientContextUuid)
            ) {
                return false;
            }
            if (isAmbiguousIssuanceError(error)) {
                await query.refetch();
                if (
                    !isCurrentClientApiContext(
                        operationContextUuid,
                        useOrgAuthStore.getState().selectedClientContextUuid
                    )
                ) {
                    return false;
                }
                const message =
                    "The create result was lost. The key can exist, but its secret cannot be recovered. Check the list, then revoke and replace that key. The Console did not retry creation.";
                setIssuanceWarning(message);
                setCreateError(message);
            } else {
                setCreateError(errorMessage(error));
            }
            return false;
        }
    };

    const rotateKey = async (id: string, input: RotateClientApiKeyInput): Promise<boolean> => {
        if (!keys.some((key) => key.id === id)) return false;
        setRotateError(null);
        setIssuanceWarning(null);
        const outcome = await issueReplacement(id, input);
        if (outcome.status === "success") {
            setRotateTarget(null);
            setIssuedSecret({
                contextUuid: outcome.result.key.issuerClientUuid,
                value: outcome.result.secret,
                rotation: {
                    label: outcome.result.previousKey.label,
                    endsAt: outcome.result.previousKey.rotationEndsAt!
                }
            });
            return true;
        }
        if (outcome.status === "ambiguous") {
            const message =
                "The rotation result was lost. A replacement can exist, but its secret cannot be recovered. Check the list, then revoke and replace that key. The Console did not retry rotation.";
            setIssuanceWarning(message);
            setRotateError(message);
        } else if (outcome.status === "error") {
            setRotateError(errorMessage(outcome.error));
        }
        return false;
    };

    const revokeKey = async (id: string): Promise<void> => {
        const operationContextUuid = issuerClientUuid;
        if (!operationContextUuid) return;
        setRevokeError(null);
        try {
            await revokeMutation.mutateAsync({
                contextUuid: operationContextUuid,
                id
            });
            if (isCurrentClientApiContext(operationContextUuid, useOrgAuthStore.getState().selectedClientContextUuid)) {
                setRevokeTarget(null);
            }
        } catch (error) {
            if (isCurrentClientApiContext(operationContextUuid, useOrgAuthStore.getState().selectedClientContextUuid)) {
                setRevokeError(errorMessage(error));
            }
        }
    };

    return {
        issuerClientUuid,
        query,
        queryErrorMessage:
            query.error instanceof OrgApiError && query.error.status === 401
                ? "Your Console session is no longer valid. Sign in again."
                : errorMessage(query.error),
        keys,
        selectedContext,
        activeCount,
        issuanceBlocked: contextBlocksIssuance || activeCount >= 5,
        rotationBlocked: contextBlocksIssuance,
        issuedSecret,
        secret,
        issuanceWarning,
        createError,
        revokeError,
        rotateError,
        scopedRevokeTarget,
        scopedRotateTarget,
        isCreating,
        rotatingKeyIds,
        revokeMutation,
        createKey,
        rotateKey,
        revokeKey,
        discardSecret: (): void => setIssuedSecret(null),
        openRevoke: (key: ClientApiKeyMetadata): void => setRevokeTarget(key),
        closeRevoke: (): void => {
            setRevokeTarget(null);
            setRevokeError(null);
        },
        openRotate: (key: ClientApiKeyMetadata): void => {
            setRotateError(null);
            setRotateTarget(key);
        },
        closeRotate: (): void => {
            setRotateTarget(null);
            setRotateError(null);
        }
    };
}
